import { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, AlertTriangle, ChevronRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const RowItem = ({ item, rank, worst = false }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(`/commandes?highlight=${item.order_id}`)}
      className="flex items-center gap-3 w-full text-left p-2 rounded-md hover:bg-muted/60 transition-colors"
      data-testid={`top-order-${rank}${worst ? "-worst" : ""}`}
    >
      <span className={`shrink-0 h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${
        worst ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"
      }`}>
        #{rank}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-muted-foreground truncate">{item.reference || "—"}</span>
          <Badge variant="outline" className="text-[10px] px-1 py-0">{item.client_name}</Badge>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {formatCurrency(item.amount_ht)} HT • cible {item.target_margin_percent}%
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`font-mono font-bold ${item.net_margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>
          {item.net_margin_percent}%
        </div>
        <div className={`text-xs font-mono ${item.net_margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>
          {formatCurrency(item.net_margin)}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </button>
  );
};

export const TopOrdersCard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ top: [], worst: [] });

  useEffect(() => {
    let mounted = true;
    api.getTopOrders({ limit: 5 })
      .then((r) => mounted && setData(r.data || { top: [], worst: [] }))
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <Card><CardContent className="py-10 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </CardContent></Card>
    );
  }

  const isEmpty = (data.top?.length || 0) === 0 && (data.worst?.length || 0) === 0;
  if (isEmpty) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="top-orders-section">
      <Card className="border-emerald-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading flex items-center gap-2 text-base">
            <Trophy className="h-5 w-5 text-emerald-600" />
            Top 5 chantiers les plus rentables
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {data.top.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Aucun chantier rentable sur la période.</p>
          ) : (
            data.top.map((it, i) => <RowItem key={it.order_id} item={it} rank={i + 1} />)
          )}
        </CardContent>
      </Card>
      <Card className="border-red-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading flex items-center gap-2 text-base">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Top 5 chantiers à surveiller
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {data.worst.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Aucun chantier déficitaire.</p>
          ) : (
            data.worst.map((it, i) => <RowItem key={it.order_id} item={it} rank={i + 1} worst />)
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TopOrdersCard;
